import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockDocuments } from '../data/mock';
import {
  Download,
  FileText,
  BookOpen,
  Video,
  Image,
  FolderOpen,
  ExternalLink,
  ArrowRight,
  Lock,
  CheckCircle2
} from 'lucide-react';

const RecursosPage = () => {
  const resourceCategories = [
    {
      id: 'documentos',
      title: 'Documentos Institucionales',
      description: 'Estatutos, código de ética y documentos oficiales de UPIM',
      icon: FileText,
      color: 'emerald',
      isPublic: true,
      resources: [
        { name: 'Directorio UPIM', url: 'https://upim.org/wp-content/uploads/2024/02/Directorio-UPIM-documento.pdf', type: 'PDF' },
        { name: 'Código de Ética', url: 'https://upim.org/wp-content/uploads/2023/10/CODIGO-HONOR-Y-JUSTICIA-UPIM.doc.pdf', type: 'PDF' },
        { name: 'Manual de Afiliación', url: 'https://upim.org/wp-content/uploads/2023/10/MANUAL-DE-AFILIACION-UPIM-2023.pdf', type: 'PDF' },
      ]
    },
    {
      id: 'contratos',
      title: 'Contratos y Formatos',
      description: 'Contratos modelo y formatos para operaciones inmobiliarias',
      icon: BookOpen,
      color: 'blue',
      isPublic: false,
      resources: [
        { name: 'Contrato Modelo NOM-247', type: 'DOCX' },
        { name: 'Formato de Compra-Venta', type: 'PDF' },
        { name: 'Contrato de Arrendamiento', type: 'DOCX' },
        { name: 'Carta de Exclusividad', type: 'PDF' },
      ]
    },
    {
      id: 'protocolos',
      title: 'Protocolos NOM-247',
      description: 'Material requerido por la norma para operaciones de compra-venta',
      icon: FolderOpen,
      color: 'purple',
      isPublic: false,
      resources: [
        { name: 'Guía de Procedimientos', type: 'PDF' },
        { name: 'Checklist de Documentos', type: 'PDF' },
        { name: 'Formato de Verificación', type: 'XLSX' },
      ]
    },
    {
      id: 'templates',
      title: 'Templates de Publicidad',
      description: 'Plantillas editables para tus publicaciones y marketing',
      icon: Image,
      color: 'pink',
      isPublic: false,
      resources: [
        { name: 'Templates para Instagram', type: 'PSD' },
        { name: 'Templates para Facebook', type: 'PSD' },
        { name: 'Flyers de Propiedades', type: 'AI' },
        { name: 'Tarjetas de Presentación', type: 'PDF' },
      ]
    },
    {
      id: 'masterclasses',
      title: 'Grabaciones Master Classes',
      description: 'Acceso a las grabaciones de todas las Master Classes',
      icon: Video,
      color: 'red',
      isPublic: false,
      resources: [
        { name: 'Marketing Digital 2025', type: 'VIDEO' },
        { name: 'Aspectos Legales', type: 'VIDEO' },
        { name: 'INFONAVIT Avanzado', type: 'VIDEO' },
        { name: 'Negociación Efectiva', type: 'VIDEO' },
      ]
    },
    {
      id: 'ebooks',
      title: 'Ebooks Inmobiliarios',
      description: 'Guías y libros digitales para tu desarrollo profesional',
      icon: BookOpen,
      color: 'amber',
      isPublic: false,
      resources: [
        { name: 'Guía del Asesor Exitoso', type: 'PDF' },
        { name: 'Estrategias de Captación', type: 'PDF' },
        { name: 'Cierre de Ventas', type: 'PDF' },
      ]
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-100 text-emerald-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      pink: 'bg-pink-100 text-pink-600',
      red: 'bg-red-100 text-red-600',
      amber: 'bg-amber-100 text-amber-600',
    };
    return colors[color] || colors.emerald;
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-emerald-50 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-emerald-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-blue-200 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Recursos UPIM
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Biblioteca Digital
            </h1>
            <p className="text-xl text-gray-600">
              Accede a documentos, contratos, templates, grabaciones y material 
              exclusivo para afiliados UPIM.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resourceCategories.map((category) => (
              <Card 
                key={category.id} 
                className="overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className={`h-12 w-12 rounded-xl ${getColorClasses(category.color)} flex items-center justify-center mb-4`}>
                      <category.icon className="h-6 w-6" />
                    </div>
                    {!category.isPublic && (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <Lock className="h-3 w-3 mr-1" />
                        Afiliados
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg">{category.title}</CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {category.resources.slice(0, 4).map((resource, index) => (
                      <li key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 truncate mr-2">{resource.name}</span>
                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                          {resource.type}
                        </Badge>
                      </li>
                    ))}
                    {category.resources.length > 4 && (
                      <li className="text-sm text-gray-500">
                        +{category.resources.length - 4} más...
                      </li>
                    )}
                  </ul>
                  
                  {category.isPublic ? (
                    <div className="space-y-2">
                      {category.resources.map((resource, index) => (
                        <Button 
                          key={index}
                          variant="outline" 
                          size="sm" 
                          className="w-full justify-between"
                          asChild
                        >
                          <a href={resource.url} target="_blank" rel="noopener noreferrer">
                            <span className="truncate">{resource.name}</span>
                            <ExternalLink className="h-4 w-4 ml-2 flex-shrink-0" />
                          </a>
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      asChild
                    >
                      <Link to="/login">
                        Acceder como afiliado
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* External Links */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Herramientas Externas
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Accede a plataformas y herramientas que complementan tu trabajo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <a
              href="https://upim.easybroker.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors group text-center"
            >
              <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-600 transition-colors">
                <FolderOpen className="h-7 w-7 text-emerald-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Bolsa Inmobiliaria (BIC)</h3>
              <p className="text-sm text-gray-500 mb-4">Nuestra bolsa inmobiliaria de confianza</p>
              <span className="inline-flex items-center text-emerald-600 text-sm font-medium">
                Visitar <ExternalLink className="ml-1 h-4 w-4" />
              </span>
            </a>

            <a
              href="https://portalinmobiliario.api4it.com/v1/brainbow_upim"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors group text-center"
            >
              <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                <CheckCircle2 className="h-7 w-7 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Avalúos con IA</h3>
              <p className="text-sm text-gray-500 mb-4">Genera avalúos en segundos</p>
              <span className="inline-flex items-center text-blue-600 text-sm font-medium">
                Visitar <ExternalLink className="ml-1 h-4 w-4" />
              </span>
            </a>

            <Link
              to="/cotizador"
              className="p-6 bg-gray-50 rounded-xl hover:bg-amber-50 transition-colors group text-center"
            >
              <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-600 transition-colors">
                <FileText className="h-7 w-7 text-amber-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Cotizador</h3>
              <p className="text-sm text-gray-500 mb-4">Cotiza tus operaciones fácilmente</p>
              <span className="inline-flex items-center text-amber-600 text-sm font-medium">
                Acceder <ArrowRight className="ml-1 h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Accede a todos los recursos
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Los afiliados UPIM tienen acceso completo a toda la biblioteca digital 
            con contratos, templates, grabaciones y más.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 h-12"
            asChild
          >
            <Link to="/afiliacion">
              Afíliate por $3,500/año
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default RecursosPage;
